import {
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    signal,
} from '@angular/core';
import { ProductComponent } from '../product/product.component';
import { Category } from '../hof-page/categories/categories.component';
import { TranslatePipe } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { UmamiService } from '../../../services/umami.service';
import { DOMHandlerService } from '../../../services/domhandler.service';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { LanguageService } from '../../../services/language.service';
import { ButtonComponent } from '../../common/button/button.component';
import { ViewportScroller } from '@angular/common';
import { ShopService } from '../../../services/shop.service';

@Component({
    selector: 'app-shop',
    imports: [
        ProductComponent,
        TranslatePipe,
        FormsModule,
        RouterLink,
        ButtonComponent,
    ],
    host: { class: 'block container lg:max-w-full mx-auto' },
    templateUrl: './shop.component.html',
})
export class ShopComponent {
    data = input.required<ShopInput>();
    searchTerm = signal('');
    umami = inject(UmamiService);
    domHandler = inject(DOMHandlerService);
    activatedRoute = inject(ActivatedRoute);
    languageService = inject(LanguageService);
    router = inject(Router);
    selectedCategory = linkedSignal<Category | undefined>(() => {
        const categoryParam =
            this.activatedRoute.snapshot.queryParams['category'];
        if (!categoryParam) return undefined;
        return this.data().categories.find(
            (category) => category.slug === categoryParam,
        );
    });
    minPrice = signal<number | null>(0);
    maxPrice = signal<number | null>(500);
    sortOption = signal<string>('default');
    products = computed(
        () =>
            this.data().categories.flatMap(
                (category: Category) => category.products,
            ) || [],
    );
    filteredProducts = linkedSignal(() => {
        return this.products().filter((product) => {
            const matchesSearch = product.name
                .toLowerCase()
                .includes(this.searchTerm().toLowerCase());

            const matchesCategory =
                !this.selectedCategory() ||
                product.category.documentId ===
                    this.selectedCategory()?.documentId;

            const price = product.price;
            const matchesMin = !this.minPrice() || price >= this.minPrice()!;
            const matchesMax = !this.maxPrice() || price <= this.maxPrice()!;
            const matchesPrice = matchesMin && matchesMax;

            return matchesSearch && matchesCategory && matchesPrice;
        });
    });
    selectedCategoryHasVouchers = computed(() => {
        const selectedCategory = this.selectedCategory();
        if (!selectedCategory) return false;
        return selectedCategory.products.some(
            (product) => product.productType === 'voucher',
        );
    });
    viewportScroller = inject(ViewportScroller);
    shopService = inject(ShopService);

    ngAfterViewInit() {
        setTimeout(() => {
            if (this.shopService.lastViewedProduct) {
                const el = document.getElementById(
                    this.shopService.lastViewedProduct.slug,
                );
                if (el) {
                    const yOffset = -250;
                    const y =
                        el.getBoundingClientRect().top +
                        window.pageYOffset +
                        yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            }
        }, 200);
    }

    handleCategoryClick(category: Category | undefined) {
        this.selectedCategory.set(category);
        if (category) {
            const queryParams: Params = { category: category.slug };
            this.router.navigate([], {
                queryParams,
            });
        } else {
            this.router.navigate([], {
                queryParams: { category: null },
            });
        }
        this.umami.track('category-click', {
            category: category?.name || 'all',
        });
        this.domHandler.closeCollapse('hs-basic-collapse');
    }

    sortProducts() {
        const option = this.sortOption();
        this.filteredProducts.update((filteredProducts) => {
            return [...filteredProducts].sort((a, b) => {
                if (option === 'name-asc') {
                    return a.name.localeCompare(b.name);
                } else if (option === 'name-desc') {
                    return b.name.localeCompare(a.name);
                } else if (option === 'price-asc') {
                    return a.price - b.price;
                } else if (option === 'price-desc') {
                    return b.price - a.price;
                } else if (option === 'newest') {
                    return (
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime()
                    );
                } else if (option === 'oldest') {
                    return (
                        new Date(a.createdAt).getTime() -
                        new Date(b.createdAt).getTime()
                    );
                }
                return 0;
            });
        });
    }
}

type ShopInput = {
    maxProducts: number; //TODO implement pagination/infinite scroll with ngx query
    categories: Category[];
};
